import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';  // same-origin by default

const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authApi = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (data) => api.post('/auth/reset-password', data),
  getPasswordResets: () => api.get('/auth/password-resets'),
};

// Federations
export const federationApi = {
  list: () => api.get('/federations'),
  get: (id) => api.get(`/federations/${id}`),
  create: (data) => api.post('/federations', data),
  update: (id, data) => api.put(`/federations/${id}`, data),
  delete: (id) => api.delete(`/federations/${id}`),
  // Vínculo do líder com a federação (Fluxo D) — decisão do usuário
  mine: () => api.get('/federations/mine'),
  pending: () => api.get('/federations/pending'),
  claim: (data) => api.post('/federations/claim', data),
  requestLink: (id, data) => api.post(`/federations/${id}/request-link`, data),
  approve: (id) => api.post(`/federations/${id}/approve`),
  reject: (id, data) => api.post(`/federations/${id}/reject`, data),
  resubmit: (id) => api.post(`/federations/${id}/resubmit`),
  approveRequest: (id) => api.post(`/federation-requests/${id}/approve`),
  rejectRequest: (id, data) => api.post(`/federation-requests/${id}/reject`, data),
};

// Users (lightweight listing + profile update — Wave 1/2)
// Documentos de verificação (privados) — envio, consulta e análise
// PEDIDO DE CADASTRO DE EQUIPE (perfil `equipe`): o lider pede, a federacao
// ou o super_admin aprova, e a equipe nasce SOMENTE na aprovacao.
export const equipeSolicitacaoApi = {
  solicitar: (data) => api.post('/minha-equipe/solicitar-equipe', data),
  minha: () => api.get('/minha-equipe/minha-solicitacao'),
  listar: () => api.get('/equipes/solicitacoes'),
  aprovar: (id) => api.post(`/equipes/solicitacoes/${id}/aprovar`),
  recusar: (id, motivo) => api.post(`/equipes/solicitacoes/${id}/recusar`, { reason: motivo }),
};

export const documentosApi = {
  exigidos: () => api.get('/documentos/exigidos'),
  pasta: (entityType, entityId) => api.get(`/documentos/${entityType}/${entityId}`),
  enviar: (entityType, entityId, tipo, arquivo) => {
    const dados = new FormData();
    dados.append('file', arquivo);
    return api.post(`/documentos/${entityType}/${entityId}/${tipo}`, dados);
  },
  arquivo: (entityType, entityId, tipo) => api.get(
    `/documentos/${entityType}/${entityId}/${tipo}/arquivo`, { responseType: 'blob' }),
  fila: () => api.get('/documentos/fila'),
  // De quem e a pasta padrao do usuario (o perfil `equipe` abre a equipe).
  minhaPasta: () => api.get('/documentos/minha-pasta'),
  filaFederacoes: () => api.get('/federations/pending'),
  decidir: (documentoId, decisao, motivo) => api.post(
    `/documentos/${documentoId}/decisao`, { decisao, motivo }),
  acessos: (entityType, entityId) => api.get(`/documentos/${entityType}/${entityId}/acessos`),
};

export const userApi = {
  list: (params) => api.get('/users', { params }),
  updateProfile: (id, data) => api.patch(`/users/${id}/profile`, data),
};

// Migrations (audit log — Wave 1)
export const migrationApi = {
  list: (params) => api.get('/migrations', { params }),
};

// Fight time rules (global + per-championship overrides — Wave 5)
export const fightTimeApi = {
  list: (params) => api.get('/fight-times', { params }),
  upsert: (data) => api.put('/fight-times', data),
  restoreDefaults: (params) => api.post('/fight-times/restore-defaults', null, { params }),
};

// Teams
export const teamApi = {
  list: (params) => api.get('/teams', { params }),
  get: (id) => api.get(`/teams/${id}`),
  create: (data) => api.post('/teams', data),
  update: (id, data) => api.put(`/teams/${id}`, data),
  delete: (id) => api.delete(`/teams/${id}`),
};

// Athletes
export const athleteApi = {
  list: (params) => api.get('/athletes', { params }),
  myTeam: () => api.get('/athletes/my-team'),
  myProfile: () => api.get('/athletes/my-profile'),
  approve: (id) => api.post(`/athletes/${id}/approve`),
  reject: (id, reason) => api.post(`/athletes/${id}/reject`, { reason }),
  createMyProfile: (data) => api.post('/athletes/my-profile', data),
  get: (id) => api.get(`/athletes/${id}`),
  // PASSO 4 — duplicidade e merge
  listarDuplicatas: () => api.get('/athletes/duplicatas'),
  simularMerge: (vivo, perdedor) =>
    api.get('/athletes/merge/simular', { params: { vivo, perdedor } }),
  executarMerge: (payload) => api.post('/athletes/merge', payload),
  // PASSO 2 (duplicidade): `params` permite reenviar com
  // `{ confirmar_duplicata: true }` depois que a pessoa confirmou que é outro
  // atleta — sem isso o backend responde 409 com a lista de parecidos.
  create: (data, params) => api.post('/athletes', data, { params }),
  update: (id, data) => api.put(`/athletes/${id}`, data),
  delete: (id) => api.delete(`/athletes/${id}`),
  transfer: (id, data) => api.patch(`/athletes/${id}/transfer`, data),
  getCard: (id) => api.get(`/athletes/${id}/card`),
  getQRCode: (id) => `${API_URL}/api/athletes/${id}/qrcode`,
  // FASE 3 — histórico de graduação (sem grau)
  graduations: (id) => api.get(`/athletes/${id}/graduations`),
  addGraduation: (id, data) => api.post(`/athletes/${id}/graduations`, data),
  removeGraduation: (id, graduationId) => api.delete(`/athletes/${id}/graduations/${graduationId}`),
  // FASE 4 — relatório de completude do cadastro (o que falta preencher)
  completude: () => api.get('/reports/athletes-completude'),
};

// MINI GERENCIAMENTO DE ALUNOS (turmas, chamada e mensalidades) — rotina da equipe
export const minhaEquipeApi = {
  painel: (params) => api.get('/minha-equipe/painel', { params }),
  // equipes REAIS do usuário (a turma é sempre de uma equipe)
  equipes: () => api.get('/minha-equipe/equipes'),
  // professores que EU posso escolher (só os da equipe — nunca a lista aberta)
  professores: (params) => api.get('/minha-equipe/professores', { params }),
  turmas: (params) => api.get('/minha-equipe/turmas', { params }),
  criarTurma: (data) => api.post('/minha-equipe/turmas', data),
  atualizarTurma: (id, data) => api.put(`/minha-equipe/turmas/${id}`, data),
  apagarTurma: (id) => api.delete(`/minha-equipe/turmas/${id}`),
  alunos: () => api.get('/minha-equipe/alunos'),
  chamada: (params) => api.get('/minha-equipe/chamada', { params }),
  salvarChamada: (data) => api.post('/minha-equipe/chamada', data),
  apagarChamada: (params) => api.delete('/minha-equipe/chamada', { params }),
  frequencia: (athleteId) => api.get(`/minha-equipe/frequencia/${athleteId}`),
  mensalidades: (params) => api.get('/minha-equipe/mensalidades', { params }),
  criarMensalidade: (data) => api.post('/minha-equipe/mensalidades', data),
  gerarMes: (data) => api.post('/minha-equipe/mensalidades/gerar', data),
  pagarMensalidade: (id, data) => api.post(`/minha-equipe/mensalidades/${id}/pagar`, data || {}),
  desfazerPagamento: (id) => api.delete(`/minha-equipe/mensalidades/${id}/pagamento`),
  apagarMensalidade: (id) => api.delete(`/minha-equipe/mensalidades/${id}`),
  rotina: (athleteId) => api.get(`/minha-equipe/rotina/${athleteId}`),
  // MONITOR (Fase 1): vínculo do ALUNO com a equipe. O papel dele continua
  // `athlete` — a promoção não cria conta nova nem troca o papel.
  monitores: () => api.get('/minha-equipe/monitores'),
  promoverMonitor: (athleteId, reason) => (
    api.post('/minha-equipe/monitores', { athlete_id: athleteId, reason: reason || '' })
  ),
  removerMonitor: (userId, reason) => (
    api.delete(`/minha-equipe/monitores/${userId}`, { params: { reason: reason || '' } })
  ),
};

// Weight Categories
export const weightCategoryApi = {  list: (params) => api.get('/weight-categories', { params }),
  matrix: (tableId) => api.get(`/weight-categories/matrix/${tableId}`),
  create: (data) => api.post('/weight-categories', data),
  update: (id, data) => api.put(`/weight-categories/${id}`, data),
  delete: (id) => api.delete(`/weight-categories/${id}`),
  upload: (file, tableId) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/weight-categories/upload?table_id=${tableId || ''}`, formData);
  },
};

// Championships
export const championshipApi = {
  list: (params) => api.get('/championships', { params }),
  get: (id) => api.get(`/championships/${id}`),
  create: (data) => api.post('/championships', data),
  update: (id, data) => api.put(`/championships/${id}`, data),
  delete: (id) => api.delete(`/championships/${id}`),
  generateBrackets: (id) => api.post(`/championships/${id}/generate-brackets`),
  deleteAllBrackets: (id) => api.delete(`/championships/${id}/brackets`),
  generateAbsoluteBrackets: (id) => api.post(`/championships/${id}/generate-absolute-brackets`),
  getAbsoluteEligible: (id, params) => api.get(`/championships/${id}/absolute/eligible-athletes`, { params }),
  toggleAbsolute: (id, athleteIds, enroll, modality) => api.post(`/championships/${id}/absolute/toggle`, { athlete_ids: athleteIds, enroll, modality }),
  getBrackets: (id, params) => api.get(`/championships/${id}/brackets`, { params }),
  finalize: (id) => api.post(`/championships/${id}/finalize`),
  getMedals: (id) => api.get(`/championships/${id}/medals`),
  getMedalEstimate: (id) => api.get(`/championships/${id}/medals/estimate`),
  saveMedalEstimate: (id, athletes) => api.put(`/championships/${id}/medal-estimate`, { estimated_athletes: athletes }),
  getMedalDeliveries: (id, bracketId) => api.get(`/championships/${id}/medal-deliveries`, { params: bracketId ? { bracket_id: bracketId } : {} }),
  updateMedalDelivery: (id, updates) => api.patch(`/championships/${id}/medal-deliveries`, { updates }),
  getMedalDeliverySummary: (id) => api.get(`/championships/${id}/medal-deliveries/summary`),
  getLiveRanking: (id, modality) => api.get(`/championships/${id}/live-ranking`, { params: modality ? { modality } : {} }),
  getTeamRanking: (id, modality) => api.get(`/championships/${id}/team-ranking`, { params: modality ? { modality } : {} }),
  getTelao: (id, limit = 5) => api.get(`/championships/${id}/telao`, { params: { limit } }),
  getBracketsPdf: (id, bracketId, modality) => `${API_URL}/api/championships/${id}/report/brackets-pdf${bracketId ? `?bracket_id=${bracketId}` : ''}${modality ? `${bracketId ? '&' : '?'}modality=${modality}` : ''}`,
  getStaffOverlayPdf: (id, bracketId, modality) => `${API_URL}/api/championships/${id}/report/staff-overlay-pdf${bracketId ? `?bracket_id=${bracketId}` : ''}${modality ? `${bracketId ? '&' : '?'}modality=${modality}` : ''}`,
  getStaffTemplatePdf: (id, bracketId, modality) => `${API_URL}/api/championships/${id}/report/staff-template-pdf${bracketId ? `?bracket_id=${bracketId}` : ''}${modality ? `${bracketId ? '&' : '?'}modality=${modality}` : ''}`,
  getAthletesExcel: (id) => `${API_URL}/api/championships/${id}/report/athletes-excel`,
  getSingleAthleteBracketsPdf: (id) => `${API_URL}/api/championships/${id}/report/single-athlete-brackets-pdf`,
  getSingleAthleteBracketsExcel: (id) => `${API_URL}/api/championships/${id}/report/single-athlete-brackets-excel`,
  getDirectPlacement: (bracketId) => api.get(`/brackets/${bracketId}/direct-placement`),
  saveDirectPlacement: (bracketId, data) => api.post(`/brackets/${bracketId}/direct-placement`, data),
  // FASE 5 — desfazer o Lançamento Direto: a chave volta ao resultado das lutas.
  undoDirectPlacement: (bracketId) => api.delete(`/brackets/${bracketId}/direct-placement`),
};

// Admin
export const adminApi = {
  resetCompetitionData: (data) => api.post('/admin/reset-competition-data', data),
  migrateAgeClasses: () => api.post('/admin/migrate-age-classes'),
  listUsers: (params) => api.get('/admin/users', { params }),
  createUser: (data) => api.post('/admin/users', data),
  updateUser: (id, data) => api.patch(`/admin/users/${id}`, data),
  resetUserPassword: (id, new_password) => api.post(`/admin/users/${id}/reset-password`, { new_password }),
  getUserLinks: (id) => api.get(`/admin/users/${id}/links`),
  getUserProfile: (id) => api.get(`/admin/users/${id}/profile`),
  deleteUser: (id, params = {}) => api.delete(`/admin/users/${id}`, { params }),
};

// Backup & Restore
export const backupApi = {
  create: () => api.post('/admin/backup'),
  list: () => api.get('/admin/backups'),
  download: (id) => `${API_URL}/api/admin/backups/${id}/download`,
  upload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/admin/restore', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  confirmRestore: (operationId) => api.post('/admin/restore/confirm', {
    operation_id: operationId,
    confirm: 'CONFIRMAR',
  }),
  getStatus: (taskId) => api.get(`/admin/backup/status/${taskId}`),
};

// Registrations
export const superfightApi = {
  list: (championshipId) => api.get('/superfights', { params: { championship_id: championshipId } }),
  create: (data) => api.post('/superfights', data),
  update: (id, data) => api.patch(`/superfights/${id}`, data),
  remove: (id) => api.delete(`/superfights/${id}`),
  recordResult: (id, data) => api.post(`/superfights/${id}/result`, data),
};

export const registrationApi = {
  list: (params) => api.get('/registrations', { params }),
  create: (data) => api.post('/registrations', data),
  confirm: (id) => api.put(`/registrations/${id}/confirm`),
  delete: (id) => api.delete(`/registrations/${id}`),
  previewWeight: (championshipId, registrationId, weight) =>
    api.get(`/championships/${championshipId}/registrations/${registrationId}/weight-preview`, { params: { weight } }),
  updateWeight: (championshipId, registrationId, payload) => {
    // payload can be a number (legacy) or an object { weight, athlete_age_class }
    const body = typeof payload === 'object' ? payload : { weight: payload };
    return api.patch(`/championships/${championshipId}/registrations/${registrationId}/weight`, body);
  },
};

// Age classes helper (used by EditWeightDialog for the "Realocar idade" dropdown)
export const ageClassApi = {
  listForSex: (tableId, sex) =>
    api.get(`/category-tables/${tableId}/age-classes`, { params: { sex } }),
};

// Matches
export const matchApi = {
  list: (params) => api.get('/matches', { params }),
  recordResult: (id, data) => api.post(`/matches/${id}/result`, data),
};

// Scoreboard (Placar Eletronico)
export const scoreboardApi = {
  getSessions: (championshipId, params) => api.get(`/championships/${championshipId}/scoreboard/sessions`, { params }),
  createSession: (championshipId, data) => api.post(`/championships/${championshipId}/scoreboard/sessions`, data),
  updateSession: (championshipId, sessionId, data) => api.patch(`/championships/${championshipId}/scoreboard/sessions/${sessionId}`, data),
  finalizeSession: (championshipId, sessionId, data) => api.post(`/championships/${championshipId}/scoreboard/sessions/${sessionId}/finalize`, data),
  deleteSession: (championshipId, sessionId) => api.delete(`/championships/${championshipId}/scoreboard/sessions/${sessionId}`),
};

// Check-in — FASE 7.5: recurso RESERVADO e hoje NÃO operacional. Nenhuma tela
// usa estas chamadas e nenhum fluxo depende de `checked_in`; a presença é
// conferida manualmente na mesa. Ver `memory/GUIA_OPERACIONAL.md`.
export const checkinApi = {
  checkin: (athleteId, championshipId) => api.post(`/checkin/${athleteId}?championship_id=${championshipId}`),
};

// Dashboard
export const dashboardApi = {
  getStats: () => api.get('/dashboard/stats'),
};

// Ranking Federativo (regras da federação versionadas, temporadas, ranking atletas/equipes)
export const rankingFederativoApi = {
  getRule: (federationId) => api.get(`/ranking-federativo/${federationId}/regra`),
  updateRule: (federationId, config) => api.put(`/ranking-federativo/${federationId}/regra`, config),
  copyRule: (federationId, sourceFederationId) =>
    api.post(`/ranking-federativo/${federationId}/regra/copiar`, { source_federation_id: sourceFederationId }),
  listSeasons: (federationId) => api.get(`/ranking-federativo/${federationId}/temporadas`),
  createSeason: (federationId, data) => api.post(`/ranking-federativo/${federationId}/temporadas`, data),
  getRanking: (federationId, params) => api.get(`/ranking-federativo/${federationId}/ranking`, { params }),
  getTeamRanking: (federationId, params) => api.get(`/ranking-federativo/${federationId}/ranking-equipes`, { params }),
  getChampionshipSnapshot: (championshipId) =>
    api.get(`/ranking-federativo/campeonatos/${championshipId}/snapshot`),
  processChampionship: (championshipId) =>
    api.post(`/ranking-federativo/campeonatos/${championshipId}/processar`),
};

// Belts
export const beltApi = {
  list: () => api.get('/belts'),
  create: (data) => api.post('/belts', data),
  update: (id, data) => api.put(`/belts/${id}`, data),
  delete: (id) => api.delete(`/belts/${id}`),
  seed: () => api.post('/belts/seed'),
};

// Category Tables
export const categoryTableApi = {
  list: () => api.get('/category-tables'),
  create: (data) => api.post('/category-tables', data),
  update: (id, data) => api.put(`/category-tables/${id}`, data),
  delete: (id) => api.delete(`/category-tables/${id}`),
  setDefault: (id) => api.put(`/category-tables/${id}/set-default`),
};

// Scoring Systems
// Fase I — somente LEITURA. A ESCRITA do CRUD legado foi retirada do backend
// (POST/PUT/DELETE/set-default nao existem mais); a lista continua servindo o
// seletor de escala de campeonatos LEGADOS sem federation_id.
export const scoringSystemApi = {
  list: () => api.get('/scoring-systems'),
};

// File Upload
export const fileApi = {
  upload: (file, entityType, entityId, field) => {
    const formData = new FormData();
    formData.append('file', file);
    const fieldParam = field ? `&field=${encodeURIComponent(field)}` : '';
    return api.post(`/upload/image?entity_type=${entityType}&entity_id=${entityId}${fieldParam}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getUrl: (path) => path ? `${API_URL}/api/files/${path}` : null,
};

// Public
export const publicApi = {
  // ETAPA 12 — a listagem pública aceita busca, filtros e paginação:
  //   { q, ano, modalidade, federacao, situacao, pagina, por_pagina }
  // e devolve { items, total, pagina, por_pagina, paginas }.
  getChampionships: (params) => api.get('/public/championships', { params }),
  // Opções REAIS dos filtros (anos e federações que têm campeonato).
  getChampionshipFilters: () => api.get('/public/championships/filtros'),
  getResults: (id) => api.get(`/public/championships/${id}/results`),
  getRegistrations: (id) => api.get(`/public/championships/${id}/registrations`),
  getLiveRanking: (id, modality) => api.get(`/championships/${id}/live-ranking`, { params: modality ? { modality } : {} }),
  getTeamRanking: (id, modality) => api.get(`/championships/${id}/team-ranking`, { params: modality ? { modality } : {} }),
  getAthleteProfile: (id) => api.get(`/public/athlete/${id}`),
};

// Public consolidated ranking
export const publicRankingApi = {
  getConsolidated: (params) => api.get('/public/ranking', { params }),
  // `params` opcional permite restringir a lista de campeonatos por federação (Etapa 12).
  getChampionshipsByYear: (year, params = {}) =>
    api.get('/public/ranking/championships', { params: { year, ...params } }),
};

export default api;
